import { WarningTriangle } from '@prenuvo/halo-icon';
import {
  Button,
  cn,
  CvaVariantProps,
  Icon,
  IconSource,
  Stack,
  TranslationValue,
  Typography,
} from '@prenuvo/halo-web';
import { cva } from 'class-variance-authority';

const pagePanelVariants = cva(undefined, {
  compoundVariants: [
    { class: 'border-neutral-700', part: 'border', variant: 'default' },
    { class: 'fill-neutral-700', part: 'icon', variant: 'default' },
    { class: 'border-red-700', part: 'border', variant: 'error' },
    { class: 'fill-red-700', part: 'icon', variant: 'error' },
    { class: 'border-blue-700', part: 'border', variant: 'info' },
    { class: 'fill-blue-700', part: 'icon', variant: 'info' },
    { class: 'border-green-700', part: 'border', variant: 'success' },
    { class: 'fill-green-700', part: 'icon', variant: 'success' },
    { class: 'border-orange-700', part: 'border', variant: 'warning' },
    { class: 'fill-orange-700', part: 'icon', variant: 'warning' },
  ],
  defaultVariants: {
    part: 'border',
    variant: 'default',
  },
  variants: {
    part: {
      border: '',
      icon: '',
    },
    variant: {
      default: '',
      error: '',
      info: '',
      success: '',
      warning: '',
    },
  },
});

interface PagePanelProps extends Omit<CvaVariantProps<typeof pagePanelVariants>, 'part'> {
  action?: string;
  children?: React.ReactNode;
  icon?: IconSource;
  message: string;
  onActionClick?: () => void;
  title: string;
  translationValue?: TranslationValue;
}

export function PagePanel({
  action,
  children,
  icon = WarningTriangle,
  message,
  onActionClick,
  title,
  translationValue,
  variant = 'default',
}: PagePanelProps) {
  return (
    <Stack alignment="center" className="mx-auto h-screen" justification="center">
      <Stack
        className={cn(
          'm-8 rounded-xl border p-16 text-center',
          pagePanelVariants({ part: 'border', variant }),
        )}
        gap={8}
        variant="vertical"
      >
        <Stack alignment="center" className="min-w-80" variant="vertical">
          <Icon
            className={{ root: pagePanelVariants({ part: 'icon', variant }) }}
            size="lg"
            source={icon}
          />
          <Typography translationKey={title} values={translationValue} variant="h2" />
        </Stack>

        <Stack alignment="center" gap={4} variant="vertical">
          <Typography
            className="italic dark:text-neutral-500"
            translationKey={message}
            values={translationValue}
          />

          {action && (
            <Button
              className={{ root: 'min-w-40 justify-center' }}
              onClick={onActionClick}
              size="lg"
              translationKey={action}
              translationValue={translationValue}
            />
          )}

          {children}
        </Stack>
      </Stack>
    </Stack>
  );
}
